import React, { useState } from 'react';
import { requestAllPermissions, AllPermissions, arePermissionsGranted } from '../../utils/permissions';
import './welcome-message.scss';

interface WelcomeMessageProps {
  onComplete: (permissions: AllPermissions, voiceGender: 'male' | 'female') => void;
}

const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ onComplete }) => {
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<'male' | 'female'>('male'); // Default to male

  const handleContinue = async () => {
    setIsRequestingPermissions(true);
    setPermissionError(null);

    try {
      const permissions = await requestAllPermissions();
      
      // Check if we got the essential permissions
      if (!arePermissionsGranted(permissions)) {
        // Show error but still allow continuation with limited functionality
        const errors = [];
        if (!permissions.microphone.granted) errors.push('microphone');
        if (!permissions.camera.granted) errors.push('camera');
        if (!permissions.location.granted) errors.push('location');
        
        setPermissionError(`Some permissions were denied: ${errors.join(', ')}. You can still use the app with limited functionality.`);
      }
      
      // Complete after a short delay to show any error message
      setTimeout(() => {
        onComplete(permissions, selectedVoice);
      }, permissionError ? 2000 : 500);
      
    } catch (error) {
      setPermissionError('Failed to request permissions. You can still use the app with limited functionality.');
      setTimeout(() => {
        onComplete({
          location: { granted: false, error: 'Permission request failed' },
          microphone: { granted: false, error: 'Permission request failed' },
          camera: { granted: false, error: 'Permission request failed' }
        }, selectedVoice);
      }, 2000);
    } finally {
      setIsRequestingPermissions(false);
    }
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <div className="welcome-message">
      <div className="welcome-content">
        <div className="welcome-section">
          
        <div className="welcome-header">
          <h1>مرحبًا بك في أمين!</h1>
          <p className="welcome-subtitle">
           مساعدك الذكي للمعلومات عن التأمين
          </p>
        </div>
        </div>

        {/* Voice Selection - First Thing User Sees */}
        <div className="voice-selection-section voice-selection-primary">
          <h3> اختر المساعد الذكي:</h3>
          <div className="voice-options">
            <button
              className={`voice-option ${selectedVoice === 'male' ? 'selected' : ''}`}
              onClick={() => setSelectedVoice('male')}
            >
              
              {/* <span className="voice-icon">👨</span>
              <span className="voice-label">صوت رجالي</span> */}
            <img src='man-personal.jpeg' width="100%" height="100%"/>
            </button>
            <button
              className={`voice-option ${selectedVoice === 'female' ? 'selected' : ''}`}
              onClick={() => setSelectedVoice('female')}
            >
              <img src='woman-personal.jpeg' width="100%" height="100%"/>
            </button>
          </div>
        </div>

        <div className="features-overview">
          <h2> كيف يمكنني مساعدتك؟</h2>
          <div className="features-grid">
            <div className="feature-item car">
              <div className="feature-content">
                <h3> تأمين السيارات</h3>
                <p> تعرف على الفرق بين الشامل وضد الغير والتحمل والعوامل المؤثرة</p>
              </div>
            </div>
            
            <div className="feature-item medical">
              <div className="feature-content">
                <h3> التأمين الطبي</h3>
                <p> احصل على معلومات عن شبكة المستشفيات والتغطيات والاستثناءات</p>
              </div>
            </div>
            
            <div className="feature-item family">
              <div className="feature-content">
                <h3> التأمين على الحياة</h3>
                <p> افهم الفرق بين التأمين المؤقت والشامل ودور المستفيدين</p>
              </div>
            </div>
            
            <div className="feature-item property">
              <div className="feature-content">
                <h3> تأمين الممتلكات</h3>
                <p>معلومات عن تأمين الحريق والسرقة والأعمال</p>
              </div>
            </div>
            
            <div className="feature-item interaction">
              <div className="feature-content">
                <h3> التفاعل الصوتي</h3>
                <p> محادثة طبيعية باللهجة المصرية مع مساعدك الذكي</p>
              </div>
            </div>
          </div>
        </div>

        <div className="permissions-section">
          <h3> للبدء، نحتاج إلى:</h3>
          <div className="permissions-list">
            <div className="permission-item">
              <div className="permission-details">

                <strong>
                  <img src='location.png' width="15px" />
                 الموقع</strong>
                <span> لتحسين تجربة المستخدم</span>
              </div>
            </div>
            <div className="permission-item">
              <div className="permission-details">
                <strong>
                  <img src='microphone.png' width="15px" />
                   الميكروفون</strong>
                <span>للتفاعل الصوتي</span>
              </div>
            </div>
            <div className="permission-item">
              <div className="permission-details">
                <strong>
                  <img src='camera.png' width="25px" />
                   الكاميرا</strong>
                <span> للميزات المحسّنة</span>
              </div>
            </div>
          </div>
        </div>

        {showDetails && (
          <div className="privacy-details">
            <h4>الخصوصية والأمان</h4>
            <ul>
              <li> يُستخدم موقعك عند الحاجة فقط ولا يتم تخزينه</li>
              <li> يتم معالجة بث الكاميرا محلياً ولا يتم تسجيله</li>
              <li> تتم معالجة بيانات الصوت بشكل آمن ولا يتم حفظها</li>
              <li> جميع عمليات نقل البيانات مشفرة</li>
            </ul>
          </div>
        )}

        {permissionError && (
          <div className="permission-error">
            <p>{permissionError}</p>
          </div>
        )}

        <div className="welcome-actions">
          <button 
            className="btn-primary"
            onClick={handleContinue}
            disabled={isRequestingPermissions}
          >
            {isRequestingPermissions ? 'الصلاحيات المطلوبة...' : 'أستكمال'}
          </button>
          
          <button 
            className="btn-secondary"
            onClick={toggleDetails}
          >
            {showDetails ? 'إخفاء التفاصيل' : 'تفاصيل الخصوصية'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeMessage;
